// EXTERNAL DEPENDENCIES
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'

// COMPONENTS
import {
  Alert,
  Button,
  Col,
  EditIcon,
  IconButton,
  Link,
  PreviewIcon,
  Row,
  ShareIcon,
} from '@paypalcorp/pp-react'
import { HeadingText } from '@paypalcorp/pp-react-text'
import ShareProfileSheetContainer from '@/common/components/ui/share-profile-sheet-container'
import FullWidthContainer from '@/common/components/ui/full-width-container'

// HELPERS
import {
  useBusinessProfilePageContent,
  useBusinessProfilePageMessage,
} from '../../hooks'
import { updateIsEditing, updateIsPreviewing } from '@/redux/features'

// TEST IDS
import {
  BUSINESS_PROFILE_HEADER_SECTION_TEST_ID,
  UNPUBLISHED_PROFILE_WARNING_ALERT_TEST_ID,
} from '../../constants/test-ids'

// FPTI
import {
  BUSINESS_PROFILE_EDIT_PROFILE_CLICK_FPTI,
  BUSINESS_PROFILE_PREVIEW_PROFILE_CLICK_FPTI,
  BUSINESS_PROFILE_SELLER_SHARE_LINK_CLICK_FPTI,
} from '../../fpti'

// STYLES
import styles from './styles.module.less'
import { whiteColor } from '@/styles/colors'

// TYPES
type HeaderSectionProps = {
  userCanCreateProfile: boolean
  userCanPreviewProfile: boolean
  shouldShow: boolean
  profilePublished: boolean
}

/**
 * Displays header text and buttons to share and edit the
 * profile if they are the profile owner
 */
export function HeaderSection({
  userCanCreateProfile,
  userCanPreviewProfile,
  shouldShow,
  profilePublished,
}: HeaderSectionProps): React.ReactElement {
  const dispatch = useDispatch()
  const getMessage = useBusinessProfilePageMessage()
  const getContent = useBusinessProfilePageContent()
  const mainPageHeaderText = getContent('mainPageHeader')
  const editProfileText = getContent('editProfile')
  const previewProfileText = getContent('previewProfile')
  const shareProfileText = getContent('shareProfile')

  const unpublishedProfileWarningText = getMessage(
    'unpublishedProfileWarningText',
    {
      link: (msg: string) => (
        <Link secondary href='https://www.paypal.com/us/cshelp/contact-us'>
          {msg}
        </Link>
      ),
    },
  )

  const [isShareSheetOpen, setIsShareSheetOpen] = useState(false)
  const handleShareClick = () => setIsShareSheetOpen(true)
  const handlePreviewClick = () => dispatch(updateIsPreviewing(true))
  const handleEditClick = () => dispatch(updateIsEditing(true))

  return shouldShow ? (
    <FullWidthContainer customBackgroundColor={whiteColor}>
      {!profilePublished && (
        <Alert
          type='warning'
          className={styles.unpublishedWarningContainer}
          data-testid={UNPUBLISHED_PROFILE_WARNING_ALERT_TEST_ID}
        >
          {unpublishedProfileWarningText}
        </Alert>
      )}
      <Row
        noGutters
        className={styles.headerSectionContainer}
        data-testid={BUSINESS_PROFILE_HEADER_SECTION_TEST_ID}
      >
        <Col className={styles.leftSection}>
          <HeadingText level={1}>{mainPageHeaderText}</HeadingText>
        </Col>
        {profilePublished && (
          <Col className={styles.rightSection}>
            {userCanCreateProfile && (
              <Button
                secondary
                className={styles.desktopHeaderEditButton}
                iconComponent={EditIcon}
                onClick={handleEditClick}
                fullWidthOnMobile={false}
                data-fpti={BUSINESS_PROFILE_EDIT_PROFILE_CLICK_FPTI}
              >
                {editProfileText}
              </Button>
            )}
            <Button
              secondary={userCanPreviewProfile}
              className={styles.desktopHeaderButton}
              iconComponent={ShareIcon}
              onClick={handleShareClick}
              fullWidthOnMobile={false}
              data-fpti={BUSINESS_PROFILE_SELLER_SHARE_LINK_CLICK_FPTI}
              aria-haspopup='true'
            >
              {shareProfileText}
            </Button>
            {userCanPreviewProfile && (
              <Button
                className={styles.desktopHeaderButton}
                iconComponent={PreviewIcon}
                onClick={handlePreviewClick}
                fullWidthOnMobile={false}
                data-fpti={BUSINESS_PROFILE_PREVIEW_PROFILE_CLICK_FPTI}
              >
                {previewProfileText}
              </Button>
            )}
            <Row noGutters className={styles.mobileNavContainer} align='end'>
              {userCanCreateProfile && (
                <IconButton
                  buttonType='secondary'
                  svgIconComponent={EditIcon}
                  onClick={handleEditClick}
                  aria-label={editProfileText}
                  data-fpti={BUSINESS_PROFILE_EDIT_PROFILE_CLICK_FPTI}
                >
                  {editProfileText}
                </IconButton>
              )}
              <IconButton
                buttonType={userCanPreviewProfile ? 'secondary' : 'primary'}
                svgIconComponent={ShareIcon}
                onClick={handleShareClick}
                data-fpti={BUSINESS_PROFILE_SELLER_SHARE_LINK_CLICK_FPTI}
                aria-haspopup='true'
              >
                {shareProfileText}
              </IconButton>
              {userCanPreviewProfile && (
                <IconButton
                  buttonType='primary'
                  svgIconComponent={PreviewIcon}
                  onClick={handlePreviewClick}
                  data-fpti={BUSINESS_PROFILE_PREVIEW_PROFILE_CLICK_FPTI}
                >
                  {previewProfileText}
                </IconButton>
              )}
            </Row>
          </Col>
        )}
        <ShareProfileSheetContainer
          isShareSheetOpen={isShareSheetOpen}
          setIsShareSheetOpen={setIsShareSheetOpen}
        />
      </Row>
    </FullWidthContainer>
  ) : null
}
